// src/components/PriceInfoBox/PriceInfoBox.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * PriceInfoBox
 * ------------------------------------------------------------------
 * Näyttää keskimääräisen hinnan yhdelle tai useammalle setille.
 *
 * Normaalikäyttö:
 *   - types + sets annetaan propseina → haetaan /pricing/summary?set=...
 *
 * Automode (blogin hintaraportti tms.):
 *   - autoTopSets = true
 *   - types = ["bilebandi"] | ["dj"] | ["trubaduuri"] | ["duo"]
 *   → haetaan /pricing/summary-all?types=...,
 *     valitaan topN suosituinta settiä ja näytetään ne.
 *   - hideIfNoData = true piilottaa laatikon kokonaan, jos dataa ei ole
 */

function PriceInfoBox({
  types = ["bilebandi"],
  sets = ["2x45min", "3x45min"],
  regionId = null,
  regionSlug = null,
  title = "Keskimääräiset hinnat",
  refreshMs = 15 * 60 * 1000, // 15 min
  compact = false,
  nationalLabel = "Buukkerin keskihinta",
  showRegionDetails = false,

  // uudet propsit
  autoTopSets = false,
  topN = 3,
  hideIfNoData = false,
}) {
  const [data, setData] = useState({});
  const [usedSets, setUsedSets] = useState(sets || []);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const timerRef = useRef(null);

  // --- apu: tee types → CSV
  const typesCsv = useMemo(() => {
    const arr = Array.isArray(types)
      ? types
      : String(types || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
    return arr.join(",");
  }, [types]);

  // --- välimuistin avain (ei käytetä autoTopSets-moodissa)
  const cacheKey = useMemo(() => {
    const CACHE_VERSION = "v6";
    const regionKey = regionSlug
      ? `slug:${regionSlug}`
      : regionId != null
      ? `id:${regionId}`
      : "na";
    const setKey = (sets || []).join("|");
    return `pricebox:${CACHE_VERSION}:${typesCsv}:${regionKey}:${setKey}`;
  }, [typesCsv, regionId, regionSlug, sets]);

  /**
   * Poimi valtakunnallinen keskiarvo.
   */
  function pickNational(r) {
    // 1) Painotettu keskiarvo items-rivien perusteella
    if (Array.isArray(r?.items) && r.items.length) {
      let sum = 0;
      let count = 0;

      for (const it of r.items) {
        const avg = Number(it.avg_eur ?? it.avg);
        if (!Number.isFinite(avg)) continue;

        const rawN = it.n_rows ?? it.n ?? it.count ?? 0;
        const n = Number(rawN);

        if (Number.isFinite(n) && n > 0) {
          sum += avg * n;
          count += n;
        } else {
          sum += avg;
          count += 1;
        }
      }

      if (count > 0) {
        return sum / count;
      }
    }

    // 2) Fallback: suorat kentät
    const candidates = [
      r?.national_avg,
      r?.national_avg_eur,
      r?.avg_eur,
      r?.avg,
      r?.mean,
      r?.value,
      r?.price_avg,
      r?.price,
    ];

    if (Number.isFinite(Number(r?.national_avg_cents))) {
      candidates.unshift(Number(r.national_avg_cents) / 100);
    }

    if (r?.national && (r.national.avg_eur ?? r.national.avg)) {
      candidates.unshift(r.national.avg_eur ?? r.national.avg);
    }
    if (r?.summary && (r.summary.national_avg_eur ?? r.summary.national_avg)) {
      candidates.unshift(r.summary.national_avg_eur ?? r.summary.national_avg);
    }

    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n)) return n;
    }

    return null;
  }

  // --- yhden setin nouto
  async function fetchOne(setLabel, signal) {
    const url = `/api/public/pricing/summary?set=${encodeURIComponent(
      setLabel
    )}&types=${encodeURIComponent(typesCsv)}`;
    const res = await fetch(url, { signal, credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // --- hae summary-all ja poimi suositut setit
  async function fetchTopSets(signal) {
    const url = `/api/public/pricing/summary-all?types=${encodeURIComponent(
      typesCsv
    )}`;
    const res = await fetch(url, { signal, credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();

    // json.sets: { "2x45min": { items:[{n_rows,...}], national_avg }, ... }
    const entries = Object.entries(json?.sets || {});
    const scored = entries
      .map(([label, meta]) => {
        const items = Array.isArray(meta.items) ? meta.items : meta.regions; // varmuuden vuoksi
        const totalRows = Array.isArray(items)
          ? items.reduce(
              (sum, it) => sum + (Number(it.n_rows ?? it.n ?? 0) || 0),
              0
            )
          : 0;
        return { label, totalRows };
      })
      .filter((e) => e.totalRows > 0)
      .sort((a, b) => b.totalRows - a.totalRows);

    return scored.slice(0, topN).map((e) => e.label);
  }

  // --- pääkuorma
  async function load({ useCache = true } = {}) {
    try {
      setErr("");
      setLoading(true);

      const controller = new AbortController();
      const { signal } = controller;

      // 1) automaattinen settien valinta (blogi-moodi)
      let targetSets = sets;
      if (autoTopSets) {
        targetSets = await fetchTopSets(signal);
        setUsedSets(targetSets);

        if (!targetSets.length) {
          // ei yhtään settiä → joko piilotetaan laatikko tai näytetään viesti
          setData({});
          if (hideIfNoData) {
            setLoading(false);
            return;
          }
          setErr("Hinnoitteludataa ei ole vielä tarpeeksi tälle esiintyjätyypille.");
          setLoading(false);
          return;
        }
      } else {
        setUsedSets(sets);
      }

      // 2) välimuisti vain ei-automoodissa
      if (useCache && !autoTopSets) {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          setData(JSON.parse(cached));
          setLoading(false);
          return;
        }
      }

      // 3) haetaan jokainen setti erikseen
      const results = await Promise.all(
        targetSets.map((label) =>
          fetchOne(label, signal).catch((e) => ({
            ok: false,
            error: String(e),
            _label: label,
          }))
        )
      );

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.table(results);
      }

      const bySet = {};
      for (const r of results) {
        if (!r || r.ok === false) {
          bySet[r?._label || "unknown"] = {
            _error: r?.error || "fetch failed",
          };
          continue;
        }

        const label = r.set;
        const national = pickNational(r);

        // region-osuma: ensin slugilla, sitten id:llä
        let regionEntry = null;
        if (Array.isArray(r.items)) {
          if (regionSlug) {
            const target = String(regionSlug).toLowerCase();
            regionEntry =
              r.items.find(
                (it) =>
                  String(it.region_slug || "").toLowerCase() === target ||
                  String(it.region || "").toLowerCase() === target
              ) || null;
          } else if (regionId != null) {
            regionEntry =
              r.items.find(
                (it) => Number(it.region_id) === Number(regionId)
              ) || null;
          }
        }

        bySet[label] = {
          national_avg: Number.isFinite(Number(national))
            ? Number(national)
            : null,
          region: regionEntry
            ? {
                region_id: regionEntry.region_id,
                region: regionEntry.region,
                region_slug: regionEntry.region_slug,
                n_rows: Number(regionEntry.n_rows),
                median_eur: Number(
                  regionEntry.median_eur ?? regionEntry.median ?? NaN
                ),
                avg_eur: Number(regionEntry.avg_eur ?? regionEntry.avg ?? NaN),
                min_eur: Number(regionEntry.min_eur ?? regionEntry.min ?? NaN),
                max_eur: Number(regionEntry.max_eur ?? regionEntry.max ?? NaN),
              }
            : null,
        };
      }

      if (!autoTopSets) {
        sessionStorage.setItem(cacheKey, JSON.stringify(bySet));
      }
      setData(bySet);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  // --- auto-haku + interval
  useEffect(() => {
    load({ useCache: true });
    if (refreshMs > 0) {
      timerRef.current = setInterval(
        () => load({ useCache: !autoTopSets }), // automoodissa ei cachea
        refreshMs
      );
      return () => clearInterval(timerRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, refreshMs, autoTopSets, typesCsv, regionId, regionSlug]);

  // --- muotoile eurot (pyöristys lähimpään sataan) ---
  const eur = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "—";

    // esim. 1320 → 1300, 1360 → 1400
    const rounded = Math.round(num / 100) * 100;

    return `${rounded.toLocaleString("fi-FI", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} €`;
  };

  // jos ei dataa ja halutaan piilottaa kokonaan (esim. duo ilman hintoja)
  const hasAnyData =
    usedSets &&
    usedSets.some((label) => {
      const row = data[label];
      return row && (row.national_avg || row?.region);
    });

  if (!loading && hideIfNoData && !hasAnyData) {
    return null;
  }

  return (
    <div
      className={`rounded-2xl shadow-md border border-white/10 bg-white/5 backdrop-blur p-4 ${
        compact ? "text-xs" : "text-base"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3
          className={`font-semibold ${
            compact
              ? "text-black/80 dark:text-white/80 text-xl"
              : "text-black/80 dark:text-white/80 text-2xl"
          }`}
        >
          {title}
        </h3>
        <div className="text-black/80 dark:text-white/80 text-xs md:text-lg opacity-70">
          {loading ? "Päivitetään…" : "Valmis"}
        </div>
      </div>

      {err && (
        <div className="text-red-500 text-xs md:text-lg mb-2">
          Datan haku epäonnistui: {err}
        </div>
      )}

      <div className="space-y-3">
        {(usedSets || []).map((label) => {
          const row = data[label] || {};
          const hasRegion = !!row.region;

          const primaryValue = hasRegion
            ? row.region.avg_eur ||
              row.region.median_eur ||
              row.national_avg
            : row.national_avg;

          return (
            <div
              key={label}
              className="rounded-2xl overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-md p-8 md:p-12 shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="text-black/80 dark:text-white/80 text-xs md:text-lg">
                  {label}
                </div>
                <div className="text-right">
                  <div className="text-black/80 dark:text-white/80 text-xs md:text-lg text-green-400 font-extrabold">
                    {eur(primaryValue)}
                  </div>
                  <div className="text-black/80 dark:text-white/80 text-xs md:text-lg opacity-70">
                    {nationalLabel}
                  </div>
                </div>
              </div>

              {hasRegion && showRegionDetails && (
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="text-black/80 dark:text-white/80 opacity-70 text-xs md:text-lg">
                      Keskiarvo
                    </div>
                    <div className="text-black/80 dark:text-white/80 font-medium text-xs md:text-lg">
                      {eur(row.region.avg_eur)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="text-black/80 dark:text-white/80 opacity-70 text-xs md:text-lg">
                      Min–Max
                    </div>
                    <div className="text-black/80 dark:text-white/80 font-medium text-xs md:text-lg">
                      {eur(row.region.min_eur)} – {eur(row.region.max_eur)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/5 border border-white/10 p-2">
                    <div className="text-black/80 dark:text-white/80 text-xs md:text-lg opacity-70">
                      Havaintoja
                    </div>
                    <div className="text-black/80 dark:text-white/80 text-xs md:text-lg">
                      {row.region.n_rows}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-black/80 dark:text-white/80 text-xs md:text-lg opacity-60">
        Perustuu Buukkerin hinnoitteludataan. Päivittyy säännöllisesti.
      </div>
    </div>
  );
}

PriceInfoBox.propTypes = {
  types: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  sets: PropTypes.arrayOf(PropTypes.string),
  regionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  regionSlug: PropTypes.string,
  title: PropTypes.string,
  refreshMs: PropTypes.number,
  compact: PropTypes.bool,
  nationalLabel: PropTypes.string,
  showRegionDetails: PropTypes.bool,
  autoTopSets: PropTypes.bool,
  topN: PropTypes.number,
  hideIfNoData: PropTypes.bool,
};

export default PriceInfoBox;
